// Compliance / enforcement overview — 05-admin §4.0 (enforcement timeline), §4.8 (summary board).
import { api, errMsg } from "../core/api.js";
import { h, mount } from "../core/dom.js";
import { navigate } from "../core/router.js";

interface Stats {
  open_reports?: number;
  unlabeled_ai?: number;
  dsr_due?: number;
  label_rate?: number;
}

// Days between today and a target ISO date (00 §7.6 enforcement dates).
function daysUntil(iso: string): number {
  const target = new Date(iso + "T00:00:00Z").getTime();
  const now = Date.now();
  return Math.ceil((target - now) / 86_400_000);
}

function countdownBadge(iso: string): HTMLElement {
  const d = daysUntil(iso);
  if (d > 0) return h("span", { class: "badge warn" }, `D-${d}`);
  if (d === 0) return h("span", { class: "badge danger" }, "D-DAY");
  return h("span", { class: "badge ok" }, "발효됨");
}

// Static enforcement-timeline card (00 §7.6 / 05 §4.0).
function enforcementCard(): HTMLElement {
  return h(
    "section",
    { class: "card" },
    h("h2", {}, "시행 일정 모니터링 (00 §7.6)"),
    h(
      "p",
      { class: "muted small" },
      "본 §7 컴플라이언스 의무는 MVP 출시부터 즉시 MUST. 카드는 데드라인 임박 경보용입니다.",
    ),
    h(
      "table",
      { class: "tbl" },
      h(
        "thead",
        {},
        h("tr", {}, h("th", {}, "관할 / 조항"), h("th", {}, "발효일"), h("th", {}, "상태"), h("th", {}, "비고")),
      ),
      h(
        "tbody",
        {},
        h(
          "tr",
          {},
          h("td", {}, "EU AI Act 제50조 (마킹·라벨)"),
          h("td", {}, "2026-08-02"),
          h("td", {}, countdownBadge("2026-08-02")),
          h("td", { class: "muted small" }, "하드 데드라인. Omnibus 마킹 유예(2026-12-02) 미확정"),
        ),
        h(
          "tr",
          {},
          h("td", {}, "EU AI Act GPAI 학습 의무"),
          h("td", {}, "2025-08-02"),
          h("td", {}, countdownBadge("2025-08-02")),
          h("td", { class: "muted small" }, "학습 콘텐츠 요약 공개(53조) → 요약 관리(§4.5)"),
        ),
        h(
          "tr",
          {},
          h("td", {}, "한국 AI기본법 제31조"),
          h("td", {}, "2026-01-22"),
          h("td", {}, h("span", { class: "badge warn" }, "계도기간 ~2027")),
          h("td", { class: "muted small" }, "≥1년 계도(시정명령 우선). 사전 고지 위반 과태료 최대 3천만원"),
        ),
      ),
    ),
  );
}

function kpi(label: string, value: string, to?: string): HTMLElement {
  return h(
    "div",
    { class: "kpi", onclick: to ? () => navigate(to) : undefined, role: to ? "link" : "" },
    h("div", { class: "kpi-value" }, value),
    h("div", { class: "kpi-label" }, label),
  );
}

export async function dashboardView(_p: Record<string, string>, root: HTMLElement): Promise<void> {
  const board = h("section", { class: "card" }, h("h2", {}, "컴플라이언스 요약 보드 (§4.8)"), h("div", { class: "state muted" }, "지표 불러오는 중…"));

  mount(
    root,
    h(
      "div",
      { class: "stack" },
      h("h1", {}, "대시보드"),
      h("p", { class: "muted" }, "신뢰·안전 + 컴플라이언스 + 운영 개요 (05-admin §4)."),
      enforcementCard(),
      board,
    ),
  );

  // §4.8 summary board — best-effort; admin /stats may not yet be implemented.
  try {
    const s = await api.get<Stats>("/v1/admin/stats");
    const grid = h(
      "div",
      { class: "kpi-grid" },
      kpi("미처리 신고", s.open_reports != null ? String(s.open_reports) : "—", "/moderation"),
      kpi("미표시 AI 잔량", s.unlabeled_ai != null ? String(s.unlabeled_ai) : "—", "/moderation"),
      kpi("DSR 기한 임박", s.dsr_due != null ? String(s.dsr_due) : "—", "/compliance"),
      kpi("AI 라벨 부착률", s.label_rate != null ? `${Math.round(s.label_rate * 100)}%` : "—"),
    );
    board.replaceChildren(h("h2", {}, "컴플라이언스 요약 보드 (§4.8)"), grid);
  } catch (e) {
    board.replaceChildren(
      h("h2", {}, "컴플라이언스 요약 보드 (§4.8)"),
      h("div", { class: "state error" }, `지표 불러오기 실패: ${errMsg(e)}`),
      h(
        "div",
        { class: "kpi-grid" },
        kpi("미처리 신고", "—", "/moderation"),
        kpi("미표시 AI 잔량", "—", "/moderation"),
        kpi("DSR 기한 임박", "—", "/compliance"),
        kpi("AI 라벨 부착률", "—"),
      ),
    );
  }
}
